import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { SignupFormData, SignupErrors } from '@/features/user/types/types';
import { createFormValidator, commonRules } from '@/lib/utils';
import { useSignup } from '@/features/user/hooks/useSignup';
import { SocialSignupButtons } from './SocialSignupButtons';
import { FormInput } from './FormInput';
import { PasswordInput } from './PasswordInput';
import { SubmitButton } from './SubmitButton';
import { SendCodeButton } from './SendCodeButton';
import { Toast } from './Toast';

interface SignupFormProps {
  onSubmit?: (data: SignupFormData) => Promise<void>;
}

export const SignupForm: React.FC<SignupFormProps> = ({ onSubmit }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sendVerificationCode, completeSignup, clearFormData, isSubmitting, isEmailVerified, pendingSignupData } = useSignup();
  const [formData, setFormData] = useState<SignupFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<SignupErrors>({});
  const [showToast, setShowToast] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // Preserve returnUrl when navigating to login
  const returnUrl = searchParams.get('returnUrl');
  const loginUrl = returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login';

  // Create form validator - only validate fields that are on this form
  const validator = createFormValidator({
    firstName: commonRules.required('First name is required'),
    lastName: commonRules.required('Last name is required'),
    email: commonRules.email('Please enter a valid email'),
    password: commonRules.required('Password is required'),
    confirmPassword: commonRules.required('Please confirm your password'),
  });

  // Restore form data when returning from confirmation page
  useEffect(() => {
    if (pendingSignupData) {
      setFormData(pendingSignupData);
    }
  }, [pendingSignupData]);

  // Cooldown timer effect
  useEffect(() => {
    let timer: number;
    if (cooldownSeconds > 0) {
      timer = window.setTimeout(() => {
        setCooldownSeconds(prev => prev - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const handleSendCode = async () => {
    // Validate required fields before sending code
    if (!formData.firstName.trim()) {
      setErrors(prev => ({ ...prev, firstName: 'First name is required' }));
      return;
    }
    
    if (!formData.lastName.trim()) {
      setErrors(prev => ({ ...prev, lastName: 'Last name is required' }));
      return;
    }
    
    if (!formData.email.trim()) {
      setErrors(prev => ({ ...prev, email: 'Email is required to send code' }));
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email' }));
      return;
    }

    setIsSendingCode(true);
    
    try {
      await sendVerificationCode(formData);
      
      // Start cooldown timer (60 seconds)
      setCooldownSeconds(60);
      
      // Clear any errors
      setErrors({});
      
    } catch (error) {
      console.error('Error sending code:', error);
      setErrors(prev => ({ ...prev, email: 'Failed to send code. Please try again.' }));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleInputChange = (field: keyof SignupFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors = validator.validateForm(formData);
    setErrors(newErrors);
    return validator.isFormValid(formData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    // Check if email is verified
    if (!isEmailVerified) {
      setErrors(prev => ({ 
        ...prev, 
        email: 'Please verify your email before creating account' 
      }));
      return;
    }

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setErrors(prev => ({ 
        ...prev, 
        confirmPassword: 'Passwords do not match' 
      }));
      return;
    }
    
    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        // Use the signup hook for API integration
        await completeSignup(formData);
      }
      
      // Show success message
      setShowToast(true);
      
      // Clear form data after successful signup
      clearFormData();
      
      // Redirect to returnUrl or profile page after a short delay
      setTimeout(() => {
        const returnUrl = searchParams.get('returnUrl');
        navigate(returnUrl || '/profile');
      }, 2000);
      
    } catch (error) {
      console.error('Signup error:', error);
      setErrors(prev => ({ 
        ...prev, 
        email: 'Failed to create account. Please try again.' 
      }));
    }
  };

  const handleSocialSignup = (provider: string) => {
    console.log(`Social signup with ${provider}`);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Social Signup Buttons */}
        <SocialSignupButtons onSocialSignup={handleSocialSignup} />

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-800"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 bg-black text-gray-500 text-sm">Or sign up with email</span>
          </div>
        </div>

        {/* Name Fields Row */}
        <div className="grid grid-cols-2 gap-4">
          <FormInput
            label="First Name"
            type="text"
            value={formData.firstName}
            onChange={(value: string) => handleInputChange('firstName', value)}
            error={errors.firstName}
            placeholder="John"
          />
          
          <FormInput
            label="Last Name"
            type="text"
            value={formData.lastName}
            onChange={(value: string) => handleInputChange('lastName', value)}
            error={errors.lastName}
            placeholder="Doe"
          />
        </div>

        {/* Email Field */}
        <FormInput
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(value: string) => handleInputChange('email', value)}
          error={errors.email}
          placeholder="john@example.com"
          isVerified={isEmailVerified}
          actionButton={
            <SendCodeButton
              onClick={handleSendCode}
              isLoading={isSendingCode}
              isDisabled={!formData.email.trim()}
              cooldownSeconds={cooldownSeconds}
            />
          }
        />

        {/* Password Fields */}
        <PasswordInput
          label="Password"
          value={formData.password}
          onChange={(value: string) => handleInputChange('password', value)}
          error={errors.password}
          placeholder="Enter your password"
        />

        <PasswordInput
          label="Confirm Password"
          value={formData.confirmPassword}
          onChange={(value: string) => handleInputChange('confirmPassword', value)}
          error={errors.confirmPassword}
          placeholder="Confirm your password"
        />


        {/* Submit Button */}
        <SubmitButton isSubmitting={isSubmitting} />

        {/* Terms and Privacy */}
        <p className="text-xs text-gray-500 text-center mt-6 leading-relaxed">
          By creating an account, you agree to our{' '}
          <a href="#" className="text-blue-400 hover:text-blue-300 transition-colors">Terms of Service</a>{' '}
          and{' '}
          <a href="#" className="text-blue-400 hover:text-blue-300 transition-colors">Privacy Policy</a>
        </p>

        {/* Login Link */}
        <div className="text-center mt-6">
          <p className="text-gray-400 text-sm">
            Already have an account?{' '}
            <a 
              href={loginUrl} 
              className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
            >
              Sign in instead
            </a>
          </p>
        </div>
      </form>

      <Toast 
        message="Account created successfully!" 
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={4000}
      />
    </>
  );
};
